import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid2, MenuItem, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useReferenceData } from "../../../hooks";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { DocumentForm as DocumentFormType, getDocumentFormSchema } from "../../../types/forms/entity";
import { EntityDocument } from "../../../types/model/entity/EntityDocument";

interface DocumentFormProps {
  documentData?: EntityDocument;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: DocumentFormType) => Promise<void>;
}

export default function DocumentForm({
  documentData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<DocumentFormProps>) {
  const { t } = useTranslation();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getDocumentFormSchema(t);
  const methods = useForm<DocumentFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      docTypeId: documentData?.docTypeId ?? undefined,
      docNumber: documentData?.docNumber || "",
      issueDate: documentData?.issueDate || "",
      dueDate: documentData?.dueDate || "",
      archive: documentData?.archive || "",
    },
  });

  const { data: documentTypes = [] } = useReferenceData("entity", "document_types");

  const handleFormSubmit = async (values: DocumentFormType) => {
    await onSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Controller
              name="docTypeId"
              control={methods.control}
              defaultValue={documentData?.docTypeId ?? undefined}
              render={({ field }) => (
                <TextField
                  select
                  fullWidth
                  label={`${t("entities.entity.documents.doc-type")} *`}
                  variant={isEditing ? "outlined" : "standard"}
                  disabled={!isEditing || documentTypes.length === 0 || isSaving || isDeleting}
                  value={
                    documentTypes.some((d) => d.id === field.value)
                      ? field.value
                      : ""
                  }
                  onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                  sx={{ ...formStyles }}
                  error={!!methods.formState.errors.docTypeId}
                  helperText={methods.formState.errors.docTypeId?.message}
                >
                  <MenuItem value="">—</MenuItem>
                  {documentTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={`${t("entities.entity.documents.doc-number")} *`}
                variant={isEditing ? "outlined" : "standard"}
                disabled={!isEditing || isSaving || isDeleting}
                sx={{ ...formStyles }}
                {...methods.register("docNumber")}
                error={!!methods.formState.errors.docNumber}
                helperText={methods.formState.errors.docNumber?.message}
              />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name="issueDate"
                control={methods.control}
                render={({ field }) => (
                  <DatePicker
                    label={`${t("entities.entity.documents.issue-date")} *`}
                    value={field.value ? dayjs(field.value, "YYYY-MM-DD") : null}
                    onChange={(date) => {
                      field.onChange(date ? date.format("YYYY-MM-DD") : undefined);
                    }}
                    format="DD/MM/YYYY"
                    disabled={!isEditing || isSaving}
                    sx={{ ...formStyles }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: isEditing ? "outlined" : "standard",
                        placeholder: "DD/MM/YYYY",
                        error: !!methods.formState.errors.issueDate,
                        helperText: methods.formState.errors.issueDate?.message,
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name="dueDate"
                control={methods.control}
                render={({ field }) => (
                  <DatePicker
                    label={`${t("entities.entity.documents.due-date")} *`}
                    value={field.value ? dayjs(field.value, "YYYY-MM-DD") : null}
                    onChange={(date) => {
                      field.onChange(date ? date.format("YYYY-MM-DD") : undefined);
                    }}
                    format="DD/MM/YYYY"
                    disabled={!isEditing || isSaving}
                    sx={{ ...formStyles }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: isEditing ? "outlined" : "standard",
                        placeholder: "DD/MM/YYYY",
                        error: !!methods.formState.errors.dueDate,
                        helperText: methods.formState.errors.dueDate?.message,
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <TextField
              fullWidth
              label={t("entities.entity.documents.archive")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("archive")}
              error={!!methods.formState.errors.archive}
              helperText={methods.formState.errors.archive?.message}
            />
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}